import { FinancingSimulator } from 'components/FinancingSimulator';
import { handleSimulatorPMT } from 'components/FinancingSimulator/utils';
import Button from 'components/Form/Button';
import FormCore from 'components/Form/Core';
import ContainerAuthenticated from 'containers/Authenticated';
import { CoreContext } from 'context/CoreContext';
import { useContext, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ReadAddressesByZipCode } from 'services/api';
import { Create } from 'services/pre-proposal';
import { Title } from 'ui/styled';
import { exposeStrapiError } from 'utils';
import { formatDocuments, unmaskPhone } from 'utils/parsers';
import { isCpf } from 'utils/validators';
import { FinancingProposalFormsWrapper, FormSpacing } from './styled';

export default function FinancingPreProposal() {
  const { user, reloadContent } = useContext(CoreContext);
  const refFormIdent = useRef();
  const refFormCliente = useRef();
  const refFormEndereco = useRef();
  const [loading, setLoading] = useState(false);
  const [tipoCliente, setTipoCliente] = useState('fisica');
  const [installmentValue, setInstallmentValue] = useState(0);

  const distribuidores = useMemo(() => {
    if (!user) return [];
    const dist = [];
    user?.distribuidores?.forEach((d) => {
      dist.push({ id: d.id, title: d.trade_name, name: d.trade_name });
    });
    if (user?.distribuidor?.id) {
      dist.push({
        id: user.distribuidor.id,
        title: user.distribuidor.trade_name,
        name: user.distribuidor.trade_name,
      });
    }
    const unique = Array.from(new Map(dist.map((d) => [d.id, d])).values());
    return unique.sort((a, b) => a.title.localeCompare(b.title));
  }, [user]);

  const pessoFisJudi = [
    { id: 'fisica', title: 'Física', name: 'Física' },
    { id: 'juridica', title: 'Jurídica', name: 'Jurídica' },
  ];

  const formIdent = [
    ...(user?.administrador
      ? [
          {
            ref: 'distribuidor',
            placeholder: 'Distribuidor',
            options: distribuidores,
            required: true,
            quarter: true,
          },
        ]
      : []),
    {
      ref: 'cnpj_integrador',
      placeholder: 'CNPJ Integrador',
      mask: '99.999.999/9999-99',
      required: true,
      quarter: true,
    },
    {
      ref: 'nome_integrador',
      placeholder: 'Nome Integrador',
      required: true,
      tress: true,
      quarter: true,
    },
    {
      ref: 'telefone_vendedor',
      placeholder: 'Telefone vendedor',
      mask: '(99)99999-9999',
      required: true,
      quarter: true,
    },
    {
      ref: 'nome_vendedor',
      placeholder: 'Nome vendedor',
      required: true,
      tress: true,
      quarter: true,
    },
  ];

  const formCliente = [
    {
      ref: 'tcc2',
      placeholder: 'Tipo de Pessoa',
      options: pessoFisJudi,
      required: true,
      tress: true,
      onBlur: async () => {
        const value = refFormCliente?.current?.getItem('tcc2');
        setTipoCliente(value);
      },
    },
    {
      ref: 'cpf',
      placeholder: 'CPF/CNPJ',
      type: 'cpf',
      required: true,
      tress: true,
    },
    {
      ref: 'nome_completo',
      placeholder: 'Nome Completo',
      required: true,
      tress: true,
    },
    {
      ref: 'data_nascimento',
      type: 'date',
      placeholder:
        tipoCliente === 'juridica' ? 'Data abertura' : 'Data de Nascimento',
      required: true,
      quarter: true,
    },
    {
      ref: 'valor_financiamento',
      type: 'money',
      placeholder: 'Valor da compra',
      required: true,
      quarter: true,
      onBlur: (e) => handleSimulatorPMT(e, setInstallmentValue),
    },
    {
      ref: 'celular',
      placeholder: 'Celular',
      mask: '(99)99999-9999',
      required: true,
      quarter: true,
    },
    {
      ref: 'email',
      placeholder: 'E-mail',
      type: 'email',
      required: false,
      quarter: true,
    },
  ];

  const formEndereco = [
    {
      ref: 'cep',
      placeholder: 'CEP',
      mask: '99999-999',
      quarter: true,
      required: false,
      onBlur: async (event) => {
        const cep = event.target.value.replace(/\D/g, '');
        if (cep.length === 8) {
          try {
            const data = await ReadAddressesByZipCode(cep);
            if (data && refFormEndereco.current) {
              refFormEndereco.current.setItem(data.logradouro, 'logradouro');
              refFormEndereco.current.setItem(data.bairro, 'bairro');
              refFormEndereco.current.setItem(data.localidade, 'cidade');
              refFormEndereco.current.setItem(data.uf, 'uf');
            }
          } catch (err) {
            console.warn('Erro ao buscar CEP:', err);
            toast.warn('CEP não encontrado.');
          }
        }
      },
    },
    {
      ref: 'logradouro',
      placeholder: 'Logradouro',
      quarter: true,
      required: false,
    },
    { ref: 'numero', placeholder: 'Número', quarter: true, required: false },
    { ref: 'complemento', placeholder: 'Complemento', quarter: true },
    { ref: 'bairro', placeholder: 'Bairro', tress: true, required: false },
    { ref: 'cidade', placeholder: 'Cidade', tress: true, required: false },
    { ref: 'uf', placeholder: 'UF', tress: true, required: false },
  ];

  // Função para limpar todos os formulários
  const limparFormularios = () => {
    const refs = [refFormIdent, refFormCliente, refFormEndereco];
    const config = [formIdent, formCliente, formEndereco];
    refs.forEach((ref, index) => {
      if (ref.current) {
        config[index].forEach((item) => {
          if (item.ref) {
            ref.current.setItem?.('', item.ref);
          }
        });
      }
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const save = async () => {
    if (loading) return;
    setLoading(true);
    const ident = refFormIdent.current?.getForm();
    const cliente = refFormCliente.current?.getForm();
    const endereco = refFormEndereco.current?.getForm();
    if (!cliente || !ident) {
      toast.error('Preencha todos os campos obrigatórios.');
      setLoading(false);
      return;
    }
    const cpfCnpjRaw = cliente.cpf || '';
    const codigoCli = formatDocuments(cpfCnpjRaw);
    const len = codigoCli.replace(/\D/g, '').length;
    if (len !== 11 && len !== 14) {
      toast.error('O CPF/CNPJ do cliente não é válido!');
      setLoading(false);
      return;
    }
    let distribuidorId = user?.administrador
      ? ident.distribuidor
      : user?.distribuidor?.id;
    if (!distribuidorId) {
      toast.error(
        'Distribuidor não informado. Verifique seu usuário ou selecione um distribuidor.'
      );
      setLoading(false);
      return;
    }
    if (cliente.tcc2 !== 'juridica') {
      if (!isCpf(codigoCli)) {
        toast.error('Favor informar CPF valido.');
        setLoading(false);
        return;
      }
    }
    // Valída se o celular informado contem 11 digitos
    if (cliente.celular) {
      const celularLimpo = unmaskPhone(cliente.celular);
      if (celularLimpo.length < 11) {
        toast.error(
          'Favor informar um número de celular válido com 11 dígitos.'
        );
        setLoading(false);
        return;
      }
    }
    if (cliente.valor_financiamento <= 0) {
      toast.error('O valor do financiamento deve ser maior que zero.');
      setLoading(false);
      return;
    }
    const rawData = {
      nome_completo: cliente.nome_completo?.trim() || null,
      tipo_cliente: cliente.tcc2?.trim() || null,
      [cliente.tcc2?.trim() === 'fisica' ? 'cpf' : 'cnpj']: codigoCli,
      data_nascimento: cliente.data_nascimento || null,
      email: cliente.email?.toLowerCase().trim() || null,
      celular: unmaskPhone(cliente.celular) || null,
      cep: endereco?.cep ? endereco.cep.replace(/\D/g, '') : null,
      logradouro: endereco?.logradouro?.trim() || null,
      numero: endereco?.numero?.trim() || null,
      complemento: endereco?.complemento?.trim() || null,
      bairro: endereco?.bairro?.trim() || null,
      cidade: endereco?.cidade?.trim() || null,
      uf: endereco?.uf?.trim() || null,
      valor_financiamento: cliente.valor_financiamento,
      distribuidor: { id: parseInt(distribuidorId, 10) },
      nome_vendedor: ident?.nome_vendedor?.trim() || null,
      telefone_vendedor: unmaskPhone(ident?.telefone_vendedor) || null,
      cnpj_integrador: ident?.cnpj_integrador || null,
      nome_integrador: ident?.nome_integrador?.trim() || null,
    };
    const data = Object.fromEntries(
      Object.entries(rawData).filter(
        ([_, v]) => v !== null && v !== undefined && v !== ''
      )
    );
    const payload = { data };
    try {
      const response = await Create(payload);
      if (response && !exposeStrapiError(response)) {
        toast.success(
          'Pré-proposta enviada com sucesso! Pode cadastrar outra.'
        );
        // SUCESSO: Limpa tudo e permite novo cadastro
        limparFormularios();
        reloadContent?.(true);
      }
    } catch (error) {
      console.error('Erro ao enviar pré-proposta:', error);
      const message =
        error?.error?.message ||
        error?.message ||
        'Falha ao conectar com o servidor.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContainerAuthenticated>
      <Title>Formulário de Pré-Financiamento</Title>
      <FinancingProposalFormsWrapper>
        <Title small nomargin>
          Identificação
        </Title>
        <FormCore ref={refFormIdent} formItems={formIdent} />
        <FormSpacing />

        <Title small nomargin>
          Informações do Cliente
        </Title>
        <FormCore ref={refFormCliente} formItems={formCliente} />
        <FormSpacing />

        <Title small nomargin>
          Endereço
        </Title>
        <FormCore ref={refFormEndereco} formItems={formEndereco} />
        <FormSpacing />

        <FinancingSimulator installmentValue={installmentValue} />
        <div
          style={{ display: 'flex', justifyContent: 'flex-start', gap: '12px' }}
        >
          <Button
            color="blue"
            width={'fit-content'}
            small
            nospace
            loading={loading}
            onClick={save}
            disabled={loading}
            onBlur={isCpf}
          >
            Enviar Pré-Proposta
          </Button>
        </div>
      </FinancingProposalFormsWrapper>
    </ContainerAuthenticated>
  );
}
